import React, { useState, useEffect, useCallback } from 'react'
import { PageHeader } from '@/components/layout'
import { certificateService } from '@/services/certificateService'
import type { CertificateListItem } from '@/services/certificateService'

type StatusFilter = 'all' | 'issued' | 'revoked'

export const AdminCertificatesPage: React.FC = () => {
  const [certificates, setCertificates] = useState<CertificateListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 20

  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [revokingCert, setRevokingCert] = useState<CertificateListItem | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revokeLoading, setRevokeLoading] = useState(false)

  const fetchCertificates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: { page: number; limit: number; search?: string } = { page, limit }
      if (search) params.search = search
      const response = await certificateService.listAll(params)
      const data = response.data || []
      const filtered = statusFilter === 'all'
        ? data
        : data.filter((c) => c.status === statusFilter)
      setCertificates(filtered)
      setTotal(response.meta?.total || 0)
    } catch (e) {
      setError((e as Error).message || 'Gagal memuat sertifikat')
    } finally {
      setLoading(false)
    }
  }, [page, search, statusFilter])

  useEffect(() => {
    void fetchCertificates()
  }, [fetchCertificates])

  const handleSearch = () => {
    setPage(1)
    setSearch(searchInput)
  }

  const openRevokeModal = (cert: CertificateListItem) => {
    setRevokingCert(cert)
    setRevokeReason('')
    setShowRevokeModal(true)
  }

  const handleRevoke = async () => {
    if (!revokingCert) return
    setRevokeLoading(true)
    try {
      await certificateService.adminRevoke(revokingCert.id, revokeReason || undefined)
      setShowRevokeModal(false)
      setRevokingCert(null)
      await fetchCertificates()
    } catch (e) {
      setError((e as Error).message || 'Gagal mencabut sertifikat')
    } finally {
      setRevokeLoading(false)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div>
      <PageHeader
        title="Manajemen Sertifikat"
        subtitle={`${total} sertifikat terdaftar`}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-primary text-[20px]">workspace_premium</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Sertifikat</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">{total}</p>
        </div>
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-green-400 text-[20px]">check_circle</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Aktif</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">
            {certificates.filter((c) => c.status === 'issued').length}
          </p>
        </div>
        <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-red-400 text-[20px]">block</span>
            <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Dicabut</p>
          </div>
          <p className="text-gray-900 dark:text-white text-2xl font-bold">
            {certificates.filter((c) => c.status === 'revoked').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          {(['all', 'issued', 'revoked'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1) }}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors whitespace-nowrap ${statusFilter === status
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                }`}
            >
              {status === 'all' ? 'Semua' : status === 'issued' ? 'Aktif' : 'Dicabut'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Cari nama, email, atau kursus..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 sm:w-64 px-4 py-2 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/5">
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Siswa</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Kursus</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Tanggal Terbit</th>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Loading...</td>
                </tr>
              ) : certificates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">Tidak ada sertifikat ditemukan</td>
                </tr>
              ) : (
                certificates.map((cert) => (
                  <tr key={cert.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{cert.user_name}</p>
                        <p className="text-xs text-gray-500 dark:text-text-muted">{cert.user_email}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">{cert.course_name}</td>
                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">{formatDate(cert.issued_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${cert.status === 'issued' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                        {cert.status === 'issued' ? 'Aktif' : 'Dicabut'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {cert.status === 'issued' && (
                          <button
                            onClick={() => openRevokeModal(cert)}
                            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/10 text-gray-500 dark:text-gray-400 hover:text-red-500 transition-colors"
                            title="Cabut Sertifikat"
                          >
                            <span className="material-symbols-outlined text-[18px]">block</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-white/5">
            <p className="text-sm text-gray-500 dark:text-text-muted">
              Halaman {page} dari {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Sebelumnya
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Selanjutnya
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-3 text-red-300 hover:text-white">Dismiss</button>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && revokingCert && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowRevokeModal(false)}>
          <div className="bg-gray-50 dark:bg-surface-dark rounded-lg p-6 w-full max-w-md border border-gray-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-red-400">warning</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Cabut Sertifikat</h2>
                <p className="text-sm text-gray-500 dark:text-text-muted">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>
            <div className="mb-4 p-3 rounded-lg bg-gray-100 dark:bg-white/5">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{revokingCert.user_name}</span> — {revokingCert.course_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-text-muted mt-1">Terbit: {formatDate(revokingCert.issued_at)}</p>
            </div>
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-1.5">Alasan (opsional)</label>
              <textarea
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                rows={3}
                placeholder="Masukkan alasan pencabutan..."
                className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRevokeModal(false)}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleRevoke}
                disabled={revokeLoading}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {revokeLoading ? 'Mencabut...' : 'Cabut Sertifikat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCertificatesPage
